import React from "react";
import { ChevronDown, ChevronUp, Settings } from "lucide-react";
import { InspectorContentFallback } from "./InspectorPanelContent";
import { InspectorMobileScenePeekProvider } from "./inspectorMobileScenePeekContext";
import {
  INSPECTOR_CHROME_HOVER_SURFACE,
  INSPECTOR_CHROME_NO_FOCUS_RING,
} from "../../utils/inspectorChromeButtonClasses";

const mobileSettingsButtonClass = [
  "flex w-full items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-gray-100 transition-colors select-none cursor-pointer sm:text-sm",
  INSPECTOR_CHROME_NO_FOCUS_RING,
  INSPECTOR_CHROME_HOVER_SURFACE,
  "hover:text-white active:bg-[rgba(55,55,58,1)]",
].join(" ");

const InspectorContentScroller = ({ children }) => (
  <div className="flex min-h-0 flex-1 flex-col">
    <div className="min-h-0 flex-1 overflow-y-auto">
      <React.Suspense fallback={<InspectorContentFallback />}>{children}</React.Suspense>
    </div>
  </div>
);

const MobilePeekSettingsFooter = ({ mobileScenePeekCompact, setMobileScenePeekExpanded }) => {
  const handleExpand = (event) => {
    event.stopPropagation();
    setMobileScenePeekExpanded(true);
  };

  const handleCollapse = (event) => {
    event.stopPropagation();
    setMobileScenePeekExpanded(false);
  };

  return (
    <div className="flex w-full shrink-0 flex-col border-t border-gray-700 bg-[rgba(45,45,48,1)] pb-[max(0px,env(safe-area-inset-bottom,0px))]">
      {mobileScenePeekCompact ? (
        <button
          type="button"
          data-no-panel-drag="true"
          aria-label="Open all property settings"
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={handleExpand}
          className={mobileSettingsButtonClass}
        >
          <Settings
            className="h-3.5 w-3.5 shrink-0 opacity-90 sm:h-4 sm:w-4"
            strokeWidth={2}
            aria-hidden
          />
          <span className="min-w-0 text-center">Settings</span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-gray-300 sm:h-[1.125rem] sm:w-[1.125rem]"
            strokeWidth={2.5}
            aria-hidden
          />
        </button>
      ) : (
        <button
          type="button"
          data-no-panel-drag="true"
          aria-label="Collapse settings"
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={handleCollapse}
          className={mobileSettingsButtonClass}
        >
          <ChevronUp
            className="h-4 w-4 shrink-0 text-gray-300 sm:h-[1.125rem] sm:w-[1.125rem]"
            strokeWidth={2.5}
            aria-hidden
          />
          <span className="min-w-0 text-center leading-tight">Collapse settings</span>
        </button>
      )}
    </div>
  );
};

const InspectorPanelBody = ({
  children,
  minimized,
  mobileScenePeekCompact,
  setMobileScenePeekExpanded,
  showMobilePeekSettingsFooter,
}) => (
  <InspectorMobileScenePeekProvider value={{ compact: mobileScenePeekCompact }}>
    {!minimized ? (
      showMobilePeekSettingsFooter ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <React.Suspense fallback={<InspectorContentFallback />}>{children}</React.Suspense>
          </div>
          <MobilePeekSettingsFooter
            mobileScenePeekCompact={mobileScenePeekCompact}
            setMobileScenePeekExpanded={setMobileScenePeekExpanded}
          />
        </div>
      ) : (
        <InspectorContentScroller>{children}</InspectorContentScroller>
      )
    ) : null}
  </InspectorMobileScenePeekProvider>
);

export default InspectorPanelBody;
